import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../Layout/Layout';
import '../styles/Leaderboard.css';
import api from '../utils/apiClient'; // Assuming you have an API client set up
import styled from 'styled-components';
import { handleApiError } from '../utils/errorHandler';

// Styled Components
const LeaderboardContainer = styled.main`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing[6]};
`;

const PageHeader = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  text-align: center;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fonts.sizes['4xl']};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  letter-spacing: 1px;
  text-transform: uppercase;
  position: relative;
  display: inline-block;
  
  &::after {
    content: 'LEADERBOARD';
    position: absolute;
    left: 0;
    top: 0;
    z-index: -1;
    color: transparent;
    -webkit-text-stroke: 1px ${({ theme }) => theme.colors.primaryDark};
    opacity: 0.3;
    transform: translateY(2px);
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  max-width: 600px;
  margin: 0 auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[16]} 0;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid ${({ theme }) => theme.colors.backgroundLighter};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  box-shadow: 0 0 15px ${({ theme }) => theme.colors.glow};
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  letter-spacing: 1px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    background-color: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    animation: dots 1.5s infinite;
    display: inline-block;
    margin-left: 4px;
  }
  
  @keyframes dots {
    0%, 20% { opacity: 0; }
    50% { opacity: 1; }
    80%, 100% { opacity: 0; }
  }
`;

const LeaderboardTable = styled.section`
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  box-shadow: ${({ theme }) => theme.shadows.strong}, 0 0 20px ${({ theme }) => theme.colors.cardGlow};
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${({ theme }) => theme.colors.divider};
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 220px;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  letter-spacing: 2px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  
  @media (max-width: 768px) {
    grid-template-columns: 60px 1fr auto;
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 220px;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  align-items: center;
  transition: all ${({ theme }) => theme.transitions.normal};
  background-color: ${({ theme, isCurrentUser }) =>
  isCurrentUser ? `rgba(10, 255, 112, 0.05)` : 'transparent'};
  position: relative;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeIn 0.5s forwards;
  animation-delay: ${({ index }) => `${index * 0.1}s`};
  cursor: pointer; /* Make the row appear clickable */
  
  &:hover {
    background-color: ${({ theme, isCurrentUser }) =>
  isCurrentUser ? `rgba(10, 255, 112, 0.08)` : theme.colors.backgroundLighter};
    transform: translateX(5px);
    box-shadow: ${({ theme }) => theme.shadows.cardHover};
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  ${({ isCurrentUser, theme }) => isCurrentUser && `
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: ${theme.colors.gradientPrimary};
    }
  `}
  
  @keyframes fadeIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 60px 1fr auto;
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  }
`;

const RankBadge = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  font-weight: 600;
  color: ${({ theme, rank }) => {
  if (rank === 1) return theme.colors.primary;
  if (rank === 2) return theme.colors.secondary;
  if (rank === 3) return theme.colors.info;
  return theme.colors.textPrimary;
}};
  position: relative;
  
  ${({ rank, theme }) => rank <= 3 && `
    &::after {
      content: '';
      position: absolute;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background: ${rank === 1
  ? `radial-gradient(circle, rgba(10, 255, 112, 0.1) 0%, rgba(10, 255, 112, 0) 70%)`
  : rank === 2
    ? `radial-gradient(circle, rgba(9, 182, 255, 0.1) 0%, rgba(9, 182, 255, 0) 70%)`
    : `radial-gradient(circle, rgba(76, 131, 255, 0.1) 0%, rgba(76, 131, 255, 0) 70%)`};
      z-index: -1;
    }
  `}
`;

const Trophy = styled.span`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  filter: drop-shadow(0 0 3px ${({ rank, theme }) =>
  rank === 1
    ? theme.colors.primary
    : rank === 2
      ? theme.colors.secondary
      : theme.colors.info
  });
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const UserAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.background};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  background: ${({ background }) => background};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: ${({ glowColor }) => glowColor};
    border-radius: inherit;
    z-index: -1;
    opacity: 0.5;
    filter: blur(4px);
  }
  
  &:hover {
    transform: scale(1.05);
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const UserName = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ProgressContainer = styled.div`
  width: 100%;
  height: 4px;
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
  position: relative;
`;

const ProgressBar = styled.div`
  height: 100%;
  background: ${({ theme, rank }) => {
  if (rank === 1) return theme.colors.gradientPrimary;
  if (rank === 2) return `linear-gradient(90deg, ${theme.colors.secondary}, ${theme.colors.info})`;
  if (rank === 3) return `linear-gradient(90deg, ${theme.colors.info}, ${theme.colors.accent})`;
  return theme.colors.gradientPrimary;
}};
  width: ${({ progress }) => `${progress}%`};
  transition: width 1s ease-out;
  box-shadow: 0 0 10px ${({ theme, rank }) => {
  if (rank === 1) return theme.colors.primary;
  if (rank === 2) return theme.colors.secondary;
  if (rank === 3) return theme.colors.info;
  return 'rgba(10, 255, 112, 0.4)';
}};
`;

const UserStats = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing[2]};
    justify-content: flex-end;
  }
`;

const StatValue = styled.span`
  font-weight: 600;
  font-size: ${({ theme, isPoints }) => isPoints
  ? theme.fonts.sizes.xl
  : theme.fonts.sizes.lg};
  color: ${({ theme, isPoints }) => isPoints
  ? theme.colors.primary
  : theme.colors.textPrimary};
  text-shadow: ${({ theme, isPoints }) => isPoints
  ? `0 0 10px ${theme.colors.glow}`
  : 'none'};
    
  @media (max-width: 768px) {
    font-size: ${({ theme, isPoints }) => isPoints
  ? theme.fonts.sizes.lg
  : theme.fonts.sizes.md};
  }
`;

const StatLabel = styled.span`
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fonts.sizes.xs};
  }
`;

const Leaderboard = () => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userWorkshopCode, setUserWorkshopCode] = useState(null);
  const [isWorkshopMode, setIsWorkshopMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First get user profile to check workshop status
        const userResponse = await api.users.getProfile();
        if (userResponse.workshop_code) {
          setUserWorkshopCode(userResponse.workshop_code);
          setIsWorkshopMode(true);
        }

        // Then fetch appropriate leaderboard
        let response;
        if (userResponse.workshop_code) {
          // Fetch workshop-specific leaderboard
          response = await api.users.getWorkshopLeaderboard(userResponse.workshop_code);
        } else {
          // Fetch global leaderboard
          response = await api.users.leaderBoard();
        }
        setLeaderboardData(response);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);

        // Use centralized error handling
        handleApiError(error, 'Failed to load leaderboard. Please try again.');
      }
    };

    fetchData();
  }, []);

  const getInitials = (name) => {
    if (!name) return '';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Get trophy emojis for top 3
  const getTrophy = (rank) => {
    if (rank === 1) return <Trophy rank={rank}>🏆</Trophy>;
    if (rank === 2) return <Trophy rank={rank}>🥈</Trophy>;
    if (rank === 3) return <Trophy rank={rank}>🥉</Trophy>;
    return <span>#{rank}</span>;
  };

  // Calculate progress percentage for progress bar
  const calculateProgress = (user) => {
    // This is a simplistic calculation - adjust based on your game mechanics
    const totalPoints = Math.max(...leaderboardData.map(u => u.reward_points));
    return Math.max(5, (user.reward_points / totalPoints) * 100); // Min 5% for visibility
  };

  // Get color for the avatar based on user_id and rank
  const getAvatarStyle = (userId, rank) => {
    // Special gradients for top 3
    if (rank === 1) {
      return {
        background: 'linear-gradient(135deg, #0AFF70, #06C058)',
        glow: 'rgba(10, 255, 112, 0.6)'
      };
    }
    if (rank === 2) {
      return {
        background: 'linear-gradient(135deg, #09B6FF, #0788C0)',
        glow: 'rgba(9, 182, 255, 0.6)'
      };
    }
    if (rank === 3) {
      return {
        background: 'linear-gradient(135deg, #4C83FF, #3A63C0)',
        glow: 'rgba(76, 131, 255, 0.6)'
      };
    }

    // For other ranks
    const hue = (userId * 137) % 360; // Generate consistent hue based on user_id
    return {
      background: `linear-gradient(135deg, hsl(${hue}, 80%, 50%), hsl(${hue + 30}, 70%, 40%))`,
      glow: `hsla(${hue}, 80%, 50%, 0.4)`
    };
  };

  // Handle row click to navigate to user profile
  const handleRowClick = (userId) => {
    navigate(`/leaderboard/${userId}`);
  };

  return (
    <Layout>
      <LeaderboardContainer>
        <PageHeader>
          <Title>
            {isWorkshopMode && userWorkshopCode
              ? `Workshop Leaderboard`
              : 'Leaderboard'
            }
          </Title>
          <Subtitle>
            {isWorkshopMode && userWorkshopCode
              ? `Rankings among your workshop participants`
              : 'See where you rank among other cyber security enthusiasts'
            }
          </Subtitle>
        </PageHeader>

        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>Decrypting database</LoadingText>
          </LoadingContainer>
        ) : (
          <LeaderboardTable>
            <TableHeader>
              <div>Rank</div>
              <div>User</div>
              <div>Stats</div>
            </TableHeader>

              {leaderboardData.map((user, index) => {
                const avatarStyle = getAvatarStyle(user.user_id, user.rank);

                return (
                  <TableRow
                    key={user.user_id}
                    isCurrentUser={user.is_current_user}
                    index={index}
                    onClick={() => handleRowClick(user.user_id)}
                  >
                    <RankBadge rank={user.rank}>
                      {getTrophy(user.rank)}
                    </RankBadge>

                  <UserProfile>
                    {user.profile_image ? (
                      <UserAvatar
                        as="img"
                        src={user.profile_image}
                        alt={user.name}
                        background={avatarStyle.background}
                        glowColor={avatarStyle.glow}
                      />
                    ) : (
                      <UserAvatar
                        background={avatarStyle.background}
                        glowColor={avatarStyle.glow}
                      >
                        {getInitials(user.name)}
                      </UserAvatar>
                    )}
                    <UserInfo>
                      <UserName>{user.name}</UserName>
                      <ProgressContainer>
                        <ProgressBar
                          progress={calculateProgress(user)}
                          rank={user.rank}
                        />
                      </ProgressContainer>
                    </UserInfo>
                  </UserProfile>

                    <UserStats>
                      <StatItem>
                        <StatValue isPoints>{user.reward_points}</StatValue>
                        <StatLabel>XP</StatLabel>
                      </StatItem>
                      <StatItem>
                        <StatValue>{user.completed_courses}</StatValue>
                        <StatLabel>Courses</StatLabel>
                      </StatItem>
                      <StatItem>
                        <StatValue>{user.completed_modules}</StatValue>
                        <StatLabel>Modules</StatLabel>
                      </StatItem>
                    </UserStats>
                  </TableRow>
                );
              })}
            </LeaderboardTable>
        )}
      </LeaderboardContainer>
    </Layout>
  );
};

export default Leaderboard;